import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { encuestaCompraApi } from '../api/seguimiento'

/**
 * Encuesta de EXPERIENCIA DE COMPRA — página pública por token (mail de las
 * 48 h post-entrega). Sin login, mobile-first, B/N Ligier (patrón del portal
 * de seguimiento). 6 preguntas, un minuto: NPS 1-5 (desde el 23 ago 2026;
 * antes 0-10 — los links de mails viejos siguen andando) + cuatro 1-5
 * (atención con "no tuve contacto") + comentario libre.
 */

const INK = '#111'
// Verde esmeralda de la marca (el del storefront ligier.com.ar).
const VERDE = '#2D6A3F'

function EscalaNps({ valor, onChange }) {
  return (
    <div>
      <div style={st.escalaNps}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            style={{ ...st.num, ...(valor === n ? st.numOn : {}) }}>{n}</button>
        ))}
      </div>
      <div style={st.anclas}><span>1 · Muy mala</span><span>5 · Excelente</span></div>
    </div>
  )
}

function Escala5({ valor, onChange }) {
  const [hover, setHover] = useState(0)
  return (
    <div style={st.stars}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          style={{ ...st.star, color: n <= (hover || valor) ? VERDE : '#d1d5db' }} aria-label={`${n} estrellas`}>★</button>
      ))}
    </div>
  )
}

function Pregunta({ titulo, children }) {
  return (
    <div style={st.pregunta}>
      <p style={st.preguntaTitulo}>{titulo}</p>
      {children}
    </div>
  )
}

export default function EncuestaCompraPage() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const [info, setInfo] = useState(null)
  const [error, setError] = useState('')
  const [listo, setListo] = useState(false)
  const [registrado, setRegistrado] = useState(false)
  const [porConfirmar, setPorConfirmar] = useState(false) // puntaje del link, todavía sin registrar
  const [confirmando, setConfirmando] = useState(false)

  const [nps, setNps] = useState(null)
  const [facilidad, setFacilidad] = useState(0)
  const [pago, setPago] = useState(0)
  const [atencion, setAtencion] = useState(0)
  const [sinContacto, setSinContacto] = useState(false)
  const [producto, setProducto] = useState(0)
  const [comentario, setComentario] = useState('')
  const [falta, setFalta] = useState('')
  const [enviando, setEnviando] = useState(false)

  // Puntaje elegido EN EL MAIL (?nps5=N, 1-5; ?nps=N 0-10 en mails viejos):
  // la página lo muestra YA MARCADO y lo registra recién cuando la persona
  // toca "Confirmar" (o completa el resto). Hasta el 6 sep 2026 se registraba
  // solo al cargar la página, confiando en que los escáneres de links no
  // ejecutan JS — falso: los de correo corporativo (Safe Links, Proofpoint)
  // renderizan la página y "apretaron" los 5 links en un segundo, dejando
  // puntajes inventados (incluso un 1/5 que alertó a un vendedor). Un robot
  // no confirma; una persona toca una vez más.
  const npsDelMail = useRef(null)
  if (npsDelMail.current === null) {
    const n5 = parseInt(searchParams.get('nps5') ?? '', 10)
    const n10 = parseInt(searchParams.get('nps') ?? '', 10)
    npsDelMail.current = Number.isInteger(n5) && n5 >= 1 && n5 <= 5 ? { nps5: n5 }
      : Number.isInteger(n10) && n10 >= 0 && n10 <= 10 ? { nps: n10 }
        : false
  }

  useEffect(() => {
    const delMail = npsDelMail.current || null
    encuestaCompraApi.obtener(token)
      .then(({ data }) => {
        setInfo(data)
        // Ya respondida y sin puntaje nuevo en el link → gracias directo.
        if (data.respondida && delMail === null) setListo(true)
      })
      .catch(() => setError('No encontramos esta encuesta. Revisá el enlace del correo.'))

    if (delMail !== null) {
      // En pantalla siempre 1-5: el 0-10 legacy se ubica en su banda NPS
      // (9-10→5, 7-8→4, 5-6→3, 3-4→2, 0-2→1) solo para preseleccionar.
      setNps(delMail.nps5 ?? Math.max(1, Math.ceil(delMail.nps / 2)))
      setPorConfirmar(true)
    }
  }, [token])

  // Confirmación del puntaje que vino en el link: registra SOLO el nps (el
  // resto del form sigue siendo opcional), como antes hacía la carga.
  const confirmarPuntaje = async () => {
    if (nps === null) return
    setConfirmando(true)
    try {
      await encuestaCompraApi.responder(token, { nps5: nps })
      setRegistrado(true); setPorConfirmar(false)
    } catch {
      setFalta('No se pudo registrar el puntaje. Probá de nuevo.')
    } finally {
      setConfirmando(false)
    }
  }

  const enviar = async () => {
    if (nps === null || !facilidad || !pago || !producto || (!atencion && !sinContacto)) {
      setFalta('Te falta contestar alguna pregunta (la de atención tiene la opción "no tuve contacto").')
      return
    }
    setEnviando(true); setFalta('')
    try {
      await encuestaCompraApi.responder(token, {
        nps5: nps,
        facilidad,
        pago,
        atencion: sinContacto ? null : atencion,
        producto,
        comentario: comentario || null,
      })
      setListo(true)
    } catch (e) {
      setFalta(e?.response?.data?.message || 'No se pudo enviar. Probá de nuevo.')
      setEnviando(false)
    }
  }

  return (
    <div style={st.page}>
      <div style={st.card}>
        {/* Logo real de Ligier (mismo asset del favicon, 1000×1000). */}
        <div style={st.logoWrap}>
          <img src="/favicon.png" alt="Ligier" style={st.logoImg} />
        </div>

        {error && <p style={st.error}>{error}</p>}
        {!error && !listo && !info && <p style={st.sub}>Cargando…</p>}

        {!error && listo && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ fontSize: '1.4rem', margin: 0 }}>🙌</p>
            <h1 style={st.h1}>¡Gracias por tu tiempo!</h1>
            <p style={st.sub}>Leemos todas las respuestas — nos ayudan a mejorar de verdad.</p>
            {info?.proxima_seleccion?.disponible && (
              <ProximaSeleccion token={token} programada={info.proxima_seleccion.programada_para} cadaDias={info.proxima_seleccion.cada_dias} sugeridos={info.proxima_seleccion.dias_sugeridos} conWhatsApp={!!info.proxima_seleccion.whatsapp} />
            )}
          </div>
        )}

        {!error && !listo && info && (
          <>
            <p style={st.eyebrow}>Pedido {info.numero || ''}</p>
            <h1 style={st.h1}>¿Cómo fue tu experiencia de compra?</h1>
            {registrado ? (
              <p style={{ ...st.sub, color: VERDE, fontWeight: 600 }}>
                ✓ Tu puntaje quedó registrado. Si tenés un minuto, completá el
                resto — y si tocaste mal, elegí otro número y listo.
              </p>
            ) : porConfirmar ? (
              <p style={st.sub}>Marcamos el puntaje que elegiste en el correo. Confirmalo con un toque, o cambialo si te equivocaste.</p>
            ) : (
              <p style={st.sub}>6 preguntas, un minuto. Las leemos todas.</p>
            )}

            <Pregunta titulo="¿Cómo calificás tu experiencia de compra?">
              {/* Con el puntaje del mail ya registrado, cambiarlo acá lo
                  re-registra al toque (el último click gana) — sin obligar a
                  completar todo el form para corregir un dedo en el celular. */}
              <EscalaNps valor={nps} onChange={(n) => {
                setNps(n)
                if (registrado) encuestaCompraApi.responder(token, { nps5: n }).catch(() => {})
              }} />
              {porConfirmar && !registrado && (
                <button type="button" onClick={confirmarPuntaje} disabled={confirmando} style={st.confirmar}>
                  {confirmando ? 'Registrando…' : `Confirmar mi puntaje: ${nps} de 5`}
                </button>
              )}
            </Pregunta>

            <Pregunta titulo="¿Qué tan fácil fue encontrar y comprar lo que buscabas en el sitio?">
              <Escala5 valor={facilidad} onChange={setFacilidad} />
            </Pregunta>

            <Pregunta titulo="¿Cómo fue pagar? (claridad y rapidez)">
              <Escala5 valor={pago} onChange={setPago} />
            </Pregunta>

            <Pregunta titulo="Si hablaste con nuestro equipo, ¿cómo fue la atención?">
              {!sinContacto && <Escala5 valor={atencion} onChange={setAtencion} />}
              <label style={st.sinContacto}>
                <input type="checkbox" checked={sinContacto}
                  onChange={e => { setSinContacto(e.target.checked); if (e.target.checked) setAtencion(0) }} />
                <span>No tuve contacto con el equipo</span>
              </label>
            </Pregunta>

            <Pregunta titulo="¿El producto estuvo a la altura de lo que esperabas?">
              <Escala5 valor={producto} onChange={setProducto} />
            </Pregunta>

            <Pregunta titulo="¿Qué podemos mejorar para la próxima?">
              {/* data-nocaps: es texto libre del cliente — la regla global de
                  MAYÚSCULAS de LGR acá gritaría mientras escribe. */}
              <textarea data-nocaps value={comentario} onChange={e => setComentario(e.target.value)} maxLength={2000}
                style={st.textarea} placeholder="Contanos lo que quieras (opcional)" />
            </Pregunta>

            {falta && <p style={st.error}>{falta}</p>}
            <button onClick={enviar} disabled={enviando} style={st.btn}>
              {enviando ? 'Enviando…' : 'Enviar respuestas'}
            </button>
          </>
        )}
      </div>
      <p style={st.legal}>Vinoteca Ligier · Buenos Aires, Argentina</p>
    </div>
  )
}

/**
 * Próxima Selección desde la encuesta (Martin, 5 sep 2026): el mismo "te la
 * dejamos lista" del checkout, ofrecido después de responder — el cliente ya
 * nos está leyendo. Es un CICLO (Martin, 14 oct 2026): "cada 15/21/30 días"
 * u otra cantidad de días, y se repite hasta que el cliente nos frene.
 *
 * Con el WhatsApp de Próxima Selección prendido (Martin, 4 oct 2026), antes
 * elige cómo lo contactamos: mail, WhatsApp o los dos. Si eligió WhatsApp y
 * el pedido no tiene un celular válido, se lo pedimos.
 */
const CANALES = [['email', 'Por mail'], ['whatsapp', 'Por WhatsApp'], ['ambos', 'Por los dos']]

function ProximaSeleccion({ token, programada, cadaDias, sugeridos, conWhatsApp }) {
  const [enviando, setEnviando] = useState(null)
  const [confirmado, setConfirmado] = useState(programada ? { fecha: programada, dias: cadaDias } : null)
  // "Los dos" viene marcado (Martin, 4 oct 2026); el cliente lo cambia si quiere.
  const [canal, setCanal] = useState(conWhatsApp ? 'ambos' : 'email')
  const [pedirTel, setPedirTel] = useState(false)
  const [telefono, setTelefono] = useState('')
  const [ultimo, setUltimo] = useState(null)   // el último pedido (días o fecha), para reintentar con el celular
  const [avisoCanal, setAvisoCanal] = useState('')
  const [error, setError] = useState('')
  const [abrirOtra, setAbrirOtra] = useState(false)
  const [otra, setOtra] = useState('')
  const otraValida = /^\d+$/.test(otra) && Number(otra) >= 1 && Number(otra) <= 365

  const programar = async (body, clave) => {
    setError(''); setEnviando(clave); setUltimo({ body, clave })
    try {
      const { data } = await encuestaCompraApi.proximaSeleccion(token, { ...body, canal, ...(pedirTel && telefono ? { telefono } : {}) })
      setConfirmado({ fecha: data.programado_para, dias: data.cada_dias })
      setAvisoCanal(data.canal === 'email' ? 'por mail'
        : `${data.canal === 'ambos' ? 'por mail y ' : ''}por WhatsApp${data.telefono ? ` al ${data.telefono}` : ''}`)
    } catch (e) {
      const errTel = e?.response?.data?.errors?.telefono?.[0]
      if (errTel) { setPedirTel(true); setError(errTel); return }
      setError(e?.response?.data?.message || 'No pudimos agendarlo. Probá de nuevo en un rato.')
    } finally { setEnviando(null) }
  }

  const botonSec = (activo) => ({
    padding: '10px 14px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', background: '#fff',
    border: `1px solid ${activo ? VERDE : '#bbb'}`, color: activo ? VERDE : INK, borderRadius: 2,
  })

  return (
    <div style={{ marginTop: 28, paddingTop: 22, borderTop: '1px solid #e6e6e6', textAlign: 'center' }}>
      <p style={st.eyebrow}>Próxima selección</p>
      <h2 style={{ ...st.h1, fontSize: '1.15rem' }}>¿Preparamos tu próxima selección?</h2>
      <p style={st.sub}>Te la dejamos lista para revisar cuando vos digas — con lo que te gustó y alguna recomendación de la casa.</p>
      {confirmado ? (
        <p style={{ ...st.sub, color: VERDE, fontWeight: 600, marginTop: 10 }}>
          ✓ Listo. Te escribimos {avisoCanal ? `${avisoCanal} ` : ''}{confirmado.dias ? <><strong>cada {confirmado.dias} días</strong> con tu selección preparada — la próxima, el <strong>{String(confirmado.fecha).slice(0, 5)}</strong>.</> : <>el <strong>{String(confirmado.fecha).slice(0, 5)}</strong> con tu selección preparada.</>}
        </p>
      ) : (
        <>
          {conWhatsApp && (
            <>
              <p style={{ ...st.sub, marginTop: 12, color: INK }}>¿Cómo querés que te avisemos?</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                {CANALES.map(([c, label]) => (
                  <button key={c} type="button" disabled={enviando !== null} onClick={() => { setCanal(c); setError(''); if (c === 'email') setPedirTel(false) }} style={botonSec(canal === c)}>
                    {label}
                  </button>
                ))}
              </div>
              {canal && <p style={{ ...st.sub, marginTop: 14, color: INK }}>¿Cada cuántos días?</p>}
            </>
          )}
          {pedirTel && canal !== 'email' && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10 }}>
              <input type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Tu celular (ej.: 11 6419-9868)"
                aria-label="Tu celular" style={{ padding: '8px 10px', border: '1px solid #bbb', borderRadius: 2, font: 'inherit', fontSize: 16, minWidth: 0, flex: '1 1 auto', maxWidth: 260 }} />
              <button type="button" disabled={!telefono.trim() || enviando !== null || !ultimo} onClick={() => programar(ultimo.body, ultimo.clave)} style={{ ...botonSec(true), background: INK, color: '#fff', borderColor: INK }}>
                {enviando ? '…' : 'Agendar'}
              </button>
            </div>
          )}
          {canal && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            {(sugeridos?.length ? sugeridos : [15, 21, 30]).map((d) => (
              <button key={d} type="button" disabled={enviando !== null} onClick={() => programar({ dias: d }, d)} style={botonSec(false)}>
                {enviando === d ? '…' : `Cada ${d} días`}
              </button>
            ))}
            <button type="button" disabled={enviando !== null} onClick={() => setAbrirOtra((v) => !v)} style={botonSec(abrirOtra)}>
              Otra cantidad
            </button>
          </div>
          )}
          {canal && abrirOtra && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
              <span style={{ ...st.sub, margin: 0, color: INK }}>Cada</span>
              <input type="number" inputMode="numeric" min={1} max={365} value={otra} onChange={(e) => setOtra(e.target.value.replace(/\D/g, ''))}
                aria-label="Cada cuántos días" style={{ padding: '8px 10px', border: '1px solid #bbb', borderRadius: 2, font: 'inherit', fontSize: 16, width: 80 }} />
              <span style={{ ...st.sub, margin: 0, color: INK }}>días</span>
              <button type="button" disabled={!otraValida || enviando !== null} onClick={() => programar({ dias: Number(otra) }, 'otra')} style={{ ...botonSec(true), background: INK, color: '#fff', borderColor: INK }}>
                {enviando === 'otra' ? '…' : 'Agendar'}
              </button>
            </div>
          )}
          {error && <p style={{ ...st.sub, color: '#b91c1c', marginTop: 8 }}>{error}</p>}
        </>
      )}
    </div>
  )
}

const st = {
  page: { minHeight: '100vh', background: '#f4f1ec', padding: '24px 12px', fontFamily: 'Arial, Helvetica, sans-serif' },
  card: { maxWidth: 520, margin: '0 auto', background: '#fff', padding: '26px 22px 30px' },
  logoWrap: { textAlign: 'center', borderBottom: `3px solid ${VERDE}`, paddingBottom: 14, marginBottom: 20 },
  logoImg: { width: 110, height: 'auto', display: 'inline-block' },
  eyebrow: { textAlign: 'center', fontSize: '0.7rem', letterSpacing: 2, textTransform: 'uppercase', color: '#8a8578', margin: 0 },
  // Negritas solo en casos puntuales (pedido Martin): el título y el botón.
  h1: { textAlign: 'center', fontSize: '1.35rem', fontWeight: 700, color: INK, margin: '8px 0 4px', lineHeight: 1.3 },
  sub: { textAlign: 'center', fontSize: '0.9rem', color: '#666', margin: '0 0 8px' },
  pregunta: { borderTop: '1px solid #eee', marginTop: 18, paddingTop: 16 },
  preguntaTitulo: { fontSize: '0.95rem', fontWeight: 400, color: '#222', margin: '0 0 10px', textAlign: 'center' },
  escalaNps: { display: 'flex', gap: 8, justifyContent: 'center' },
  num: { width: 52, height: 44, border: '1px solid #d1d5db', background: '#fff', color: '#333', fontWeight: 400, cursor: 'pointer', borderRadius: 6, fontSize: '1rem' },
  numOn: { background: INK, borderColor: INK, color: '#fff' },
  anclas: { display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#9ca3af', marginTop: 6 },
  stars: { display: 'flex', justifyContent: 'center', gap: 8 },
  star: { background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer', lineHeight: 1, padding: 0 },
  sinContacto: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 10, fontSize: '0.85rem', color: '#555', cursor: 'pointer' },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: '0.9rem', fontFamily: 'inherit', height: 90, resize: 'vertical' },
  confirmar: { display: 'block', width: '100%', marginTop: 12, background: VERDE, color: '#fff', border: 'none', padding: '13px 0', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer' },
  btn: { width: '100%', marginTop: 20, background: INK, color: '#fff', border: 'none', padding: '14px 0', fontSize: '0.85rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer' },
  error: { textAlign: 'center', color: '#991b1b', fontSize: '0.9rem', margin: '12px 0 0' },
  legal: { textAlign: 'center', fontSize: '0.72rem', color: '#9ca3af', margin: '18px 8px 0' },
}
